import { Button, cn, useTranslation } from '@hbs-us/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { BulkActionBar } from '../_ops/BulkActionBar.js';
import { InventoryTable } from './components/InventoryTable.js';
import type { InventoryItemDto, InventoryListResponse } from './contractsMirror.js';
import type { InventorySearch } from './searchParams.js';
import {
  useInventoryBulkArchiveMutation,
  useInventoryQuantityMutation,
  useRestoreInventoryMutation,
} from './useInventoryQuery.js';

/**
 * The redesigned inventory list (UI-SPEC archetype A — list + detail) on the locked Velocity
 * brand. Page wrapper + header (title, subtitle, "+ Add Item" + "Manage Categories" CTAs) + a
 * labelled search input + the category-filter pill row (server counts) + the low-stock summary +
 * the semantic item table with inline qty edit + bulk archive — or the empty state.
 *
 * The coarse endpoint (22-08) owns the category split + counts + low-stock flags; this view
 * renders the returned payload and applies the free-text search IN-MEMORY over the already-loaded
 * list (small dataset, no extra round-trip). Inline qty adjust PATCHes through
 * `useInventoryQuantityMutation`; bulk archive routes the selected ids through the focus-trapped
 * `ConfirmDialog` then `useInventoryBulkArchiveMutation` (the server holds the inventory.archive
 * gate, T-22-archive). The SPA never sends a tenant id (SEC-11).
 */

export interface InventoryViewProps {
  data: InventoryListResponse;
  search: InventorySearch;
}

export function InventoryView({ data, search }: InventoryViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const items = data.items ?? [];
  const categories = data.categories ?? [];
  const lowStockCount = Number(data.lowStockCount ?? 0);
  const showArchived = Boolean(search.archived);
  const activeCategoryId = search.category ?? null;

  const [queryText, setQueryText] = useState('');
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(() => new Set());
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  // Optional reason per row, applied to that row's next inline quantity adjustment (persisted to
  // the activity log by the server). Blank → no reason sent.
  const [adjustReasons, setAdjustReasons] = useState<Readonly<Record<number, string>>>({});

  const quantityMutation = useInventoryQuantityMutation();
  const bulkArchiveMutation = useInventoryBulkArchiveMutation();
  const restoreMutation = useRestoreInventoryMutation();

  const filtered = useMemo(() => {
    const q = queryText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.name.toLowerCase().includes(q));
  }, [items, queryText]);

  const hasData = filtered.length > 0;
  const searching = queryText.trim().length > 0;

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    const selectable = filtered.filter((i) => !i.archivedAt).map((i) => i.id);
    setSelectedIds((prev) => {
      const allSelected = selectable.length > 0 && selectable.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(selectable);
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  function adjustQuantity(item: InventoryItemDto, nextQuantity: number) {
    quantityMutation.mutate(
      {
        itemId: item.id,
        quantity: nextQuantity,
        reason: adjustReasons[item.id]?.trim() || undefined,
      },
      { onSuccess: () => setAdjustReasons((prev) => ({ ...prev, [item.id]: '' })) },
    );
  }

  function restoreItem(item: InventoryItemDto) {
    restoreMutation.mutate({ ids: [item.id] });
  }

  function confirmBulkArchive() {
    const ids = Array.from(selectedIds);
    setConfirmingBulk(false);
    if (ids.length === 0) return;
    bulkArchiveMutation.mutate(
      { ids },
      {
        onSuccess: () => clearSelection(),
      },
    );
  }

  const selectedCount = selectedIds.size;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {showArchived ? t('inventory:archivedTitle') : t('inventory:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {showArchived ? t('inventory:archivedSubtitle') : t('inventory:subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="secondary">
            <Link to="/inventory/categories">{t('inventory:actions.manageCategories')}</Link>
          </Button>
          <Button onClick={() => void navigate({ to: '/inventory/items/new' })}>
            {t('inventory:actions.addItem')}
          </Button>
        </div>
      </header>

      <div className="mb-4">
        <label htmlFor="inventory-search" className="sr-only">
          {t('inventory:search.label')}
        </label>
        <div className="relative max-w-md">
          <Search
            className="pointer-events-none absolute top-3.5 left-3 size-4 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            id="inventory-search"
            type="search"
            value={queryText}
            onChange={(event) => setQueryText(event.target.value)}
            placeholder={t('inventory:search.placeholder')}
            className="h-11 w-full rounded-md border border-border bg-card pr-3 pl-9 text-foreground text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
      </div>

      {/* Category filter pills (server counts) — each is a typed <Link> navigation. */}
      <section
        aria-label={t('inventory:filter.regionLabel')}
        className="mb-4 flex flex-wrap items-center gap-2"
      >
        <Link
          to="/inventory"
          search={showArchived ? { archived: true } : {}}
          className={cn(
            'inline-flex h-9 items-center rounded-full border px-3 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            activeCategoryId === null
              ? 'border-transparent bg-secondary text-secondary-foreground'
              : 'border-border bg-card text-muted-foreground hover:bg-secondary',
          )}
        >
          {t('inventory:filter.all')}
        </Link>
        {categories.map((category) => {
          const isActive = activeCategoryId === category.id;
          return (
            <Link
              key={category.id}
              to="/inventory"
              search={
                showArchived ? { category: category.id, archived: true } : { category: category.id }
              }
              className={cn(
                'inline-flex h-9 items-center gap-1.5 rounded-full border px-3 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                isActive
                  ? 'border-transparent bg-secondary text-secondary-foreground'
                  : 'border-border bg-card text-muted-foreground hover:bg-secondary',
              )}
            >
              {category.name}
              <span className="text-muted-foreground text-xs">{category.itemCount}</span>
            </Link>
          );
        })}
        <Link
          to="/inventory"
          search={
            showArchived
              ? activeCategoryId
                ? { category: activeCategoryId }
                : {}
              : activeCategoryId
                ? { category: activeCategoryId, archived: true }
                : { archived: true }
          }
          className="ml-auto inline-flex h-9 items-center rounded-md border border-border bg-card px-3 font-medium text-foreground text-sm transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {showArchived ? t('inventory:filter.hideArchived') : t('inventory:filter.showArchived')}
        </Link>
      </section>

      {!showArchived && lowStockCount > 0 ? (
        <p className="mb-4 inline-flex items-center rounded-md bg-destructive/10 px-3 py-2 font-medium text-destructive text-sm">
          {t('inventory:lowStock.summary', { count: lowStockCount })}
        </p>
      ) : null}

      {selectedCount > 0 ? (
        <div className="mb-4">
          <BulkActionBar
            selectedCount={selectedCount}
            summaryLabel={t('inventory:bulk.selected', { count: selectedCount })}
            clearLabel={t('inventory:bulk.clear')}
            onClear={clearSelection}
            actions={[
              {
                key: 'archive',
                label: t('inventory:bulk.archive', { count: selectedCount }),
                variant: 'destructive',
                disabled: bulkArchiveMutation.isPending,
                onClick: () => setConfirmingBulk(true),
              },
            ]}
          />
        </div>
      ) : null}

      {hasData ? (
        <InventoryTable
          items={filtered}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          onAdjustQuantity={adjustQuantity}
          adjustReasons={adjustReasons}
          onAdjustReasonChange={(itemId, reason) =>
            setAdjustReasons((prev) => ({ ...prev, [itemId]: reason }))
          }
          onRestore={restoreItem}
          pendingItemId={
            quantityMutation.isPending ? (quantityMutation.variables?.itemId ?? null) : null
          }
          restorePendingItemId={
            restoreMutation.isPending ? (restoreMutation.variables?.ids?.[0] ?? null) : null
          }
        />
      ) : (
        <div className="rounded-md border border-border border-dashed bg-card p-12 text-center">
          <h2 className="font-display font-semibold text-foreground text-lg">
            {searching
              ? t('inventory:empty.searchHeading')
              : showArchived
                ? t('inventory:empty.archivedHeading')
                : t('inventory:empty.heading')}
          </h2>
          <p className="mt-1 text-muted-foreground text-sm">
            {searching
              ? t('inventory:empty.searchBody')
              : showArchived
                ? t('inventory:empty.archivedBody')
                : t('inventory:empty.body')}
          </p>
        </div>
      )}

      <ConfirmDialog
        open={confirmingBulk}
        heading={
          selectedCount > 1
            ? t('inventory:archiveDialog.headingMany')
            : t('inventory:archiveDialog.heading')
        }
        body={
          selectedCount > 1
            ? t('inventory:archiveDialog.bodyMany')
            : t('inventory:archiveDialog.body')
        }
        confirmLabel={t('inventory:archiveDialog.confirm')}
        cancelLabel={t('inventory:archiveDialog.cancel')}
        confirmPending={bulkArchiveMutation.isPending}
        onCancel={() => setConfirmingBulk(false)}
        onConfirm={confirmBulkArchive}
      />
    </div>
  );
}
